import { skipToken } from "@reduxjs/toolkit/query";
import { Button } from "primereact/button";
import { Message } from "primereact/message";
import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  useGetEngineExplanationQuery,
  useGetLatestEngineExplanationQuery,
  useRequestEngineExplanationMutation,
} from "store/queries/benefitComparison";
import {
  ComparisonBenefit,
  IEngineExplanation,
} from "types/BenefitComparison/BenefitComparison";

import ChatMarkdown from "../ContractModelDrafts/wizard/chatMarkdown";
import { relativeTime } from "./remitAccuracyWording";

export const EXPLAIN_BUTTON_LABEL = "Ask Cyrus to Explain";
export const ASK_AGAIN_LABEL = "Ask again";
export const TRY_AGAIN_LABEL = "Try again";
export const STALE_TEXT =
  "The engine's settings changed since Cyrus wrote this.";
export const QUEUED_TEXT = "Waiting for Cyrus to pick this up…";
export const QUEUED_UNPICKED_TEXT =
  "Cyrus hasn't picked this up yet — the explanation worker may not be running.";
export const RUNNING_TEXT =
  "Cyrus is working on it — this can take a minute or more.";
export const STUCK_TEXT =
  "This looks stuck — Cyrus has been on it longer than its 10-minute budget.";
/** How often the panel re-reads a QUEUED / RUNNING explanation. */
export const POLL_MS = 4000;
/** How often the elapsed time ("queued 40 s ago") re-renders while waiting. */
export const TICK_MS = 5000;
/** A worker polls every few seconds, so a row still QUEUED after this was likely never picked up. */
export const QUEUED_UNPICKED_AFTER_MS = 2 * 60 * 1000;
/** The worker's budget is 10 minutes; past this a RUNNING row with no recent heartbeat is stuck. */
export const RUNNING_STUCK_AFTER_MS = 12 * 60 * 1000;
/** A live worker heartbeats every ~30 s; a heartbeat newer than this means Cyrus is still on it. */
const HEARTBEAT_FRESH_MS = 2 * 60 * 1000;

const isActive = (explanation: IEngineExplanation | null | undefined) =>
  explanation?.status === "QUEUED" || explanation?.status === "RUNNING";

const msSince = (iso: string | null, now: number) =>
  iso === null ? null : now - new Date(iso).getTime();

/** "40 s", "3 min" — the wait so far, to the second under a minute. */
export const formatElapsed = (ms: number) => {
  const seconds = Math.max(0, Math.floor(ms / 1000));
  if (seconds < 60) return `${seconds} s`;
  return `${Math.floor(seconds / 60)} min`;
};

/**
 * RUNNING for longer than the worker's budget allows, and not heartbeating. A re-claimed second
 * attempt keeps the first attempt's `startedAt` but heartbeats, so it is not called stuck.
 */
export const isStuck = (explanation: IEngineExplanation, now: number) => {
  if (explanation.status !== "RUNNING") return false;
  const running = msSince(explanation.startedAt ?? explanation.createdAt, now);
  const heartbeat = msSince(explanation.heartbeatAt, now);
  return (
    running !== null &&
    running > RUNNING_STUCK_AFTER_MS &&
    (heartbeat === null || heartbeat > HEARTBEAT_FRESH_MS)
  );
};

/** `Date.now()`, re-read every `intervalMs` while `intervalMs > 0` — so elapsed times tick. */
const useNow = (intervalMs: number) => {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (intervalMs <= 0) return undefined;
    setNow(Date.now());
    const timer = setInterval(() => setNow(Date.now()), intervalMs);
    return () => clearInterval(timer);
  }, [intervalMs]);
  return now;
};

interface IEngineExplanationContext {
  explanation: IEngineExplanation | null | undefined;
  loading: boolean;
  isAsking: boolean;
  askFailed: boolean;
  now: number;
  request: (rerun: boolean) => void;
}

const EngineExplanationContext =
  createContext<IEngineExplanationContext | null>(null);

/**
 * "Ask Cyrus to Explain" — on the cluster page's response panel.
 *
 * One click queues a question for Cyrus (the agents app): why did the engine pick, or not pick,
 * a value for THIS check and THIS benefit. The worker gives Cyrus the identifiers and nothing
 * else; it reads the data and the code itself. The panel polls until the answer is in.
 *
 * Split in three so the panel can put the button in its toolbar and the answer above the table:
 * this provider holds the state (key it on the check + benefit), `ExplainButton` is the toolbar
 * button, and `EngineExplanationSection` is the output — nothing until there is something to show.
 *
 * - Nothing asked yet: the button, no section.
 * - QUEUED: "waiting to be picked up" and since when; past 2 minutes, that the worker may not be
 *   running (there is none on dev).
 * - RUNNING: "working on it" and since when; past 12 minutes with no heartbeat, that it looks stuck
 *   — and the button becomes "Try again".
 * - DONE: the answer, when it was written, and the button becomes "Ask again" — with a line above
 *   it when the engine's settings changed since it was written (`stale`).
 * - FAILED: why, and the button becomes "Try again".
 *
 * 🚨 The panel tracks the row the POST returned, not just "the newest row": the POST can hand
 * back an older finished answer that is still current, and that is the one to show.
 */
export function EngineExplanationProvider({
  pverifyResponseId,
  benefit,
  children,
}: {
  pverifyResponseId: string;
  benefit: ComparisonBenefit;
  children: ReactNode;
}) {
  const [requestedId, setRequestedId] = useState<string | null>(null);
  const [latestPolling, setLatestPolling] = useState(0);
  const latest = useGetLatestEngineExplanationQuery(
    { pverifyResponseId, benefit },
    { pollingInterval: requestedId ? 0 : latestPolling }
  );
  const [byIdPolling, setByIdPolling] = useState(POLL_MS);
  const byId = useGetEngineExplanationQuery(requestedId ?? skipToken, {
    pollingInterval: byIdPolling,
  });
  const [ask, { isLoading: isAsking, isError: askFailed }] =
    useRequestEngineExplanationMutation();

  const explanation = requestedId ? byId.data?.data : latest.data?.data;
  const active = isActive(explanation);
  // Poll only while Cyrus is working; stop the moment the row settles.
  useEffect(() => {
    setLatestPolling(!requestedId && active ? POLL_MS : 0);
    setByIdPolling(requestedId && (!explanation || active) ? POLL_MS : 0);
  }, [requestedId, active, explanation]);
  // The elapsed time must move while waiting, whether or not a poll changed anything.
  const now = useNow(active ? TICK_MS : 0);

  const value = useMemo<IEngineExplanationContext>(
    () => ({
      explanation,
      loading: latest.isLoading || (requestedId !== null && !explanation),
      isAsking,
      askFailed,
      now,
      request: async (rerun: boolean) => {
        try {
          const result = await ask({
            pverifyResponseId,
            benefit,
            ...(rerun ? { rerun: true } : {}),
          }).unwrap();
          setRequestedId(result.data.id);
        } catch {
          // `askFailed` renders the message
        }
      },
    }),
    [
      explanation,
      latest.isLoading,
      requestedId,
      isAsking,
      askFailed,
      now,
      ask,
      pverifyResponseId,
      benefit,
    ]
  );

  return (
    <EngineExplanationContext.Provider value={value}>
      {children}
    </EngineExplanationContext.Provider>
  );
}

const useEngineExplanation = () => useContext(EngineExplanationContext);

/**
 * The toolbar button. Before anything is asked it asks; while Cyrus works it is busy; once there is
 * an answer it is "Ask again", and on a failure or a stuck run it is "Try again". The only button
 * that asks — the section below has none, so there are never two doing the same thing.
 */
export function ExplainButton() {
  const context = useEngineExplanation();
  if (!context) return null;
  const { explanation, loading, isAsking, now, request } = context;

  const common = {
    type: "button" as const,
    icon: "pi pi-comments",
    className: "p-button-outlined p-button-sm",
    loading: isAsking,
  };

  if (explanation?.status === "DONE") {
    return (
      <Button
        {...common}
        label={ASK_AGAIN_LABEL}
        data-testid="benefitComparison.cluster.explain.again"
        onClick={() => request(true)}
      />
    );
  }
  if (
    explanation &&
    (explanation.status === "FAILED" || isStuck(explanation, now))
  ) {
    return (
      <Button
        {...common}
        label={TRY_AGAIN_LABEL}
        data-testid="benefitComparison.cluster.explain.retry"
        onClick={() => request(true)}
      />
    );
  }
  const waiting = loading || isActive(explanation);
  return (
    <Button
      {...common}
      label={EXPLAIN_BUTTON_LABEL}
      loading={isAsking || waiting}
      disabled={waiting}
      data-testid="benefitComparison.cluster.explain"
      onClick={() => request(false)}
    />
  );
}

function Waiting({
  explanation,
  now,
}: {
  explanation: IEngineExplanation;
  now: number;
}) {
  if (explanation.status === "QUEUED") {
    const queuedFor = msSince(explanation.createdAt, now) ?? 0;
    return (
      <div data-testid="benefitComparison.cluster.explanation.working">
        <span>
          <i className="pi pi-spin pi-spinner mr-2" aria-hidden="true" />
          {QUEUED_TEXT}
        </span>
        <span className="text-sm text-color-secondary ml-2">
          queued {formatElapsed(queuedFor)} ago
        </span>
        {queuedFor > QUEUED_UNPICKED_AFTER_MS && (
          <p
            className="text-sm text-orange-700 mt-2 mb-0"
            data-testid="benefitComparison.cluster.explanation.unpicked"
          >
            {QUEUED_UNPICKED_TEXT}
          </p>
        )}
      </div>
    );
  }
  const runningFor =
    msSince(explanation.startedAt ?? explanation.createdAt, now) ?? 0;
  const stuck = isStuck(explanation, now);
  return (
    <div data-testid="benefitComparison.cluster.explanation.working">
      <span>
        <i
          className={`pi ${
            stuck ? "pi-exclamation-triangle" : "pi-spin pi-spinner"
          } mr-2`}
          aria-hidden="true"
        />
        {RUNNING_TEXT}
      </span>
      <span className="text-sm text-color-secondary ml-2">
        started {formatElapsed(runningFor)} ago
      </span>
      {stuck && (
        <p
          className="text-sm text-orange-700 mt-2 mb-0"
          data-testid="benefitComparison.cluster.explanation.stuck"
        >
          {STUCK_TEXT}
        </p>
      )}
    </div>
  );
}

/**
 * The output, under its own heading. Renders nothing until something has been asked, or a
 * previous answer for this check was found on open.
 */
export function EngineExplanationSection({
  className = "",
}: {
  className?: string;
}) {
  const context = useEngineExplanation();
  if (!context) return null;
  const { explanation, askFailed, now } = context;
  // While the previous answer (or the row just asked for) loads, the toolbar button is busy.
  if (!explanation && !askFailed) return null;

  return (
    <div
      className={className}
      data-testid="benefitComparison.cluster.explanation"
    >
      <div className="flex flex-wrap align-items-baseline justify-content-between gap-2 mb-2">
        <span className="text-xs font-bold uppercase text-color-secondary">
          Cyrus&apos;s explanation
        </span>
        {explanation?.status === "DONE" && (
          <span
            className="text-sm text-color-secondary"
            title={explanation.finishedAt ?? explanation.createdAt}
          >
            {relativeTime(explanation.finishedAt ?? explanation.createdAt)}
          </span>
        )}
      </div>

      {askFailed && (
        <Message
          severity="error"
          className="mb-2"
          text="Cyrus could not be asked. Try again."
        />
      )}

      {explanation && isActive(explanation) && (
        <Waiting explanation={explanation} now={now} />
      )}

      {explanation?.status === "DONE" && (
        <div data-testid="benefitComparison.cluster.explanation.answer">
          {/*
           * 🚨 An answer written under an older configuration can blame a setting that is gone (an
           * ignore keyword since removed). Say so above it rather than let it read as current.
           */}
          {explanation.stale && (
            <p
              className="text-sm text-orange-700 mt-0 mb-2"
              data-testid="benefitComparison.cluster.explanation.stale"
            >
              <i
                className="pi pi-exclamation-triangle mr-2"
                aria-hidden="true"
              />
              {STALE_TEXT}
            </p>
          )}
          <ChatMarkdown text={explanation.answer ?? ""} />
        </div>
      )}

      {explanation?.status === "FAILED" && (
        <div data-testid="benefitComparison.cluster.explanation.failed">
          <Message
            severity="warn"
            text={`Cyrus could not explain this check: ${
              explanation.error ?? "unknown error"
            }`}
          />
        </div>
      )}
    </div>
  );
}
